import React, { useState, useEffect } from 'react'
import { 
  PaperAirplaneIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline'
import { supabase } from '../../../lib/supabase-auth'
import { useAuth } from '../../../contexts/AuthContextLazy'

interface QuestionnaireSession {
  id: string
  status: string
  progress_percentage: number
  envelope_status: string
  created_at: string
  last_activity_at: string
  expires_at: string
  reminder_count: number
  template_name: string
  questionnaire_type: string
  template_version: string
  client_name: string
  client_email: string
  client_phone: string
  sent_by_name: string
  assigned_to_name: string
  task_id: string
  task_priority: string
  task_due_date: string
}

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-purple-100 text-purple-800',
  submitted: 'bg-green-100 text-green-800',
  approved: 'bg-green-100 text-green-800'
}

const ENVELOPE_STATUS_COLORS = {
  none: '',
  sent: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
}

export const FormsDashboard: React.FC = () => {
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireSession[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const { userProfile } = useAuth()

  // Fetch questionnaires using the dashboard view
  const fetchQuestionnaires = async () => {
    try {
      const { data, error } = await supabase
        .from('advisor_questionnaire_dashboard')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching questionnaires:', error)
        return
      }

      setQuestionnaires(data || [])
    } catch (error) {
      console.error('Error in fetchQuestionnaires:', error)
    } finally {
      setLoading(false)
    }
  }

  // Set up real-time subscription
  useEffect(() => {
    fetchQuestionnaires()

    const channel = supabase
      .channel('questionnaire_dashboard')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'questionnaire_sessions'
        },
        (payload) => {
          console.log('Questionnaire updated:', payload)
          fetchQuestionnaires() // Refresh the list
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  // Filter questionnaires
  const filteredQuestionnaires = questionnaires.filter(q => {
    const matchesSearch = q.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         q.template_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         q.client_email?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter
    const matchesType = typeFilter === 'all' || q.questionnaire_type === typeFilter

    return matchesSearch && matchesStatus && matchesType
  })

  // Get status display
  const getStatusDisplay = (status: string, envelopeStatus: string) => {
    if (envelopeStatus === 'sent') {
      return { label: 'Awaiting Signature', color: ENVELOPE_STATUS_COLORS.sent }
    }
    if (envelopeStatus === 'completed') {
      return { label: 'Completed', color: ENVELOPE_STATUS_COLORS.completed }
    }
    
    const statusLabels = {
      draft: 'Draft',
      sent: 'Sent',
      in_progress: 'In Progress',
      completed: 'Completed',
      submitted: 'Submitted',
      approved: 'Approved'
    }
    
    return {
      label: statusLabels[status as keyof typeof statusLabels] || status,
      color: STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'bg-gray-100 text-gray-800'
    }
  }

  // Progress bar component
  const ProgressBar = ({ progress }: { progress: number }) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  )

  // Status chip component
  const StatusChip = ({ status, envelopeStatus }: { status: string, envelopeStatus: string }) => {
    const { label, color } = getStatusDisplay(status, envelopeStatus)
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {label}
      </span>
    )
  }

  // Priority badge component
  const PriorityBadge = ({ priority }: { priority: string }) => {
    if (!priority) return null
    
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800',
      urgent: 'bg-red-200 text-red-900'
    }
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${colors[priority as keyof typeof colors] || colors.medium}`}>
        {priority}
      </span>
    )
  }

  // Action buttons component
  const ActionButtons = ({ questionnaire }: { questionnaire: QuestionnaireSession }) => (
    <div className="flex items-center gap-2">
      <button 
        className="text-gray-400 hover:text-gray-600 p-1"
        title="View details"
      >
        <EyeIcon className="h-4 w-4" />
      </button>
      
      {questionnaire.progress_percentage >= 90 && (
        <button 
          className="text-gray-400 hover:text-gray-600 p-1"
          title="Download PDF"
        >
          <DocumentArrowDownIcon className="h-4 w-4" />
        </button>
      )}
      
      {questionnaire.status === 'sent' && questionnaire.progress_percentage < 10 && (
        <button 
          className="text-blue-600 hover:text-blue-800 p-1"
          title="Send reminder"
        >
          <PaperAirplaneIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  )

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Questionnaire Management</h1>
            <p className="text-gray-600 mt-1">Monitor and manage all client questionnaires</p>
          </div>
        </div>

        {/* Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients or templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="sent">Sent</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="submitted">Submitted</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Types</option>
            <option value="insurance">Insurance</option>
            <option value="investment">Investment</option>
            <option value="health">Health</option>
            <option value="financial_review">Financial Review</option>
          </select>

          <div className="flex items-center text-sm text-gray-600">
            <FunnelIcon className="h-4 w-4 mr-2" />
            {filteredQuestionnaires.length} of {questionnaires.length} questionnaires
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { 
            title: 'Total Sent', 
            count: questionnaires.filter(q => q.status !== 'draft').length,
            icon: PaperAirplaneIcon,
            color: 'text-blue-600 bg-blue-100'
          },
          { 
            title: 'In Progress', 
            count: questionnaires.filter(q => q.status === 'in_progress').length,
            icon: ClockIcon,
            color: 'text-yellow-600 bg-yellow-100'
          },
          { 
            title: 'Completed', 
            count: questionnaires.filter(q => q.envelope_status === 'completed' || q.status === 'approved').length,
            icon: CheckCircleIcon,
            color: 'text-green-600 bg-green-100'
          },
          { 
            title: 'Overdue', 
            count: questionnaires.filter(q => 
              q.task_due_date && new Date(q.task_due_date) < new Date() && 
              q.status !== 'completed' && q.status !== 'approved'
            ).length,
            icon: ExclamationCircleIcon,
            color: 'text-red-600 bg-red-100'
          }
        ].map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Questionnaires Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredQuestionnaires.map((questionnaire) => (
                <tr key={questionnaire.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {questionnaire.client_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {questionnaire.client_email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {questionnaire.template_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        v{questionnaire.template_version} • {questionnaire.questionnaire_type}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(questionnaire.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-24">
                      <ProgressBar progress={questionnaire.progress_percentage} />
                      <p className="text-xs text-gray-500 mt-1">{questionnaire.progress_percentage}%</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusChip 
                      status={questionnaire.status} 
                      envelopeStatus={questionnaire.envelope_status} 
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <PriorityBadge priority={questionnaire.task_priority} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {questionnaire.task_due_date ? (
                      <div className="flex items-center">
                        <CalendarDaysIcon className="h-4 w-4 mr-1" />
                        {new Date(questionnaire.task_due_date).toLocaleDateString()}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <ActionButtons questionnaire={questionnaire} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredQuestionnaires.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500">
                {questionnaires.length === 0 ? (
                  <div>
                    <PaperAirplaneIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-lg font-medium mb-2">No questionnaires sent yet</p>
                    <p>Start by sending a questionnaire from a client record.</p>
                  </div>
                ) : (
                  <div>
                    <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-lg font-medium mb-2">No questionnaires match your filters</p>
                    <p>Try adjusting your search or filter criteria.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FormsDashboard